// What the combo, scatter, gauge, waterfall and treemap charts draw, as pure functions of a result, so every
// rule (which measures are lines, running totals, "Other" sums, point caps, gauge state) is testable without
// a browser. Rendering lives in charts.ts.

import type { QueryDisplay, QueryResult, QueryResultColumn } from './types';
import { unitSignature } from './chart-guidance';
import { displayCell, isAdditive } from './format';
import { computeGoalProgress, thresholdState, type GoalProgress, type ThresholdState, type Thresholds } from './metric-chrome';

function numberOf(value: unknown): number | null {
    if (value == null || value === '') return null;
    const number = Number(value); return Number.isFinite(number) ? number : null;
}

function rowLabel(result: QueryResult, index: number, dims: QueryResultColumn[], display: QueryDisplay | null, withYear = false): string {
    const row = result.rows[index];
    return dims.map(d => displayCell(row[d.alias], d, display, { row, withYear })).join(' · ');
}

// ─────────────────────────── Combo ───────────────────────────

/**
 * The measures drawn as lines in a combo chart, by alias. The author's choice wins when it leaves at least one
 * column and one line; otherwise the default: every measure whose unit differs from the first measure's, or the
 * last measure when they all share one unit.
 */
export function comboLineAliases(measures: QueryResultColumn[], chosen?: readonly string[] | null): { lines: Set<string>; defaulted: boolean } {
    const aliases = measures.map(m => m.alias);
    const picked = (chosen ?? []).filter(alias => aliases.includes(alias));
    if (picked.length > 0 && picked.length < measures.length) return { lines: new Set(picked), defaulted: false };
    if (measures.length < 2) return { lines: new Set(), defaulted: true };
    const first = unitSignature(measures[0]);
    const different = measures.filter(m => unitSignature(m) !== first).map(m => m.alias);
    return { lines: new Set(different.length ? different : [aliases[aliases.length - 1]]), defaulted: true };
}

// ─────────────────────────── Waterfall ───────────────────────────

export type WaterfallKind = 'increase' | 'decrease' | 'total' | 'empty';
export interface WaterfallStep {
    label: string;
    /** The step's own change (the total for the total bar); null when the group has no value. */
    value: number | null;
    /** Running total before and after the step. */
    start: number;
    end: number;
    kind: WaterfallKind;
    /** Result row behind the step; null for the total bar and the combined "Other" step. */
    rowIndex: number | null;
}

/** Waterfalls need values that add up: counts and totals, never averages, distinct counts or percentages. */
export function waterfallMeasureOk(measure: Pick<QueryResultColumn, 'aggregate' | 'reaggregable'>): boolean {
    return (measure.aggregate === 'sum' || measure.aggregate === 'count') && measure.reaggregable !== false;
}

/**
 * Each step moves the running total by its value, starting from zero; a missing value moves nothing. Steps
 * beyond `maxSteps` are combined into one exact "Other" step, and the total bar (when shown) spans zero to the
 * final running total.
 */
export function waterfallSteps(labels: string[], values: Array<number | null>, options: { showTotal?: boolean; maxSteps?: number; rowIndices?: Array<number | null> } = {}):
    { steps: WaterfallStep[]; total: number; combined: number } {
    const maxSteps = Math.max(2, options.maxSteps ?? 60);
    let entries = labels.map((label, i) => ({ label, value: values[i] ?? null, rowIndex: options.rowIndices ? options.rowIndices[i] ?? null : i }));
    let combined = 0;
    if (entries.length > maxSteps) {
        const rest = entries.slice(maxSteps - 1);
        combined = rest.length;
        const sum = rest.reduce((acc, e) => acc + (e.value ?? 0), 0);
        entries = [...entries.slice(0, maxSteps - 1), { label: 'Other', value: rest.some(e => e.value != null) ? sum : null, rowIndex: null }];
    }
    const steps: WaterfallStep[] = [];
    let running = 0;
    for (const entry of entries) {
        const start = running;
        running += entry.value ?? 0;
        steps.push({ label: entry.label, value: entry.value, start, end: running, rowIndex: entry.rowIndex,
            kind: entry.value == null ? 'empty' : entry.value < 0 ? 'decrease' : 'increase' });
    }
    if (options.showTotal !== false) steps.push({ label: 'Total', value: running, start: 0, end: running, kind: 'total', rowIndex: null });
    return { steps, total: running, combined };
}

// ─────────────────────────── Treemap ───────────────────────────

export interface TreemapTile { x: string; y: number; rowIndex: number | null }
export interface TreemapSeries { name: string; data: TreemapTile[] }
export interface TreemapData { series: TreemapSeries[]; notes: string[]; error?: string; total: number }

/** Treemaps size tiles as parts of a whole: counts, totals and percentages of the total add up. */
export function treemapMeasureOk(measure: Pick<QueryResultColumn, 'aggregate' | 'kind' | 'reaggregable'>): boolean {
    return isAdditive(measure as QueryResultColumn) && (measure.reaggregable !== false || measure.aggregate === 'percentOfTotal');
}

function topWithOther<T extends { y: number }>(items: T[], limit: number, other: (rest: T[]) => T): { kept: T[]; combined: number } {
    const sorted = [...items].sort((a, b) => b.y - a.y);
    if (sorted.length <= limit) return { kept: sorted, combined: 0 };
    const rest = sorted.slice(limit - 1);
    return { kept: [...sorted.slice(0, limit - 1), other(rest)], combined: rest.length };
}

/**
 * Tiles for one or two grouping fields. One field: one tile per group. Two fields: the first field forms the
 * outer groups and the second the tiles inside each. Negative values cannot be drawn; groups with no value or zero
 * take no area. The largest tiles are kept and the rest combined into an exact "Other".
 */
export function treemapData(result: QueryResult, display: QueryDisplay | null = null, limits: { tiles?: number; groups?: number; perGroup?: number } = {}): TreemapData {
    const dims = result.columns.filter(c => c.kind === 'dimension');
    const measures = result.columns.filter(c => c.kind === 'measure');
    const empty = { series: [], notes: [], total: 0 };
    if (measures.length !== 1 || dims.length < 1 || dims.length > 2) return { ...empty, error: 'A treemap needs one or two fields to break down by and one calculation.' };
    const measure = measures[0];
    if (!treemapMeasureOk(measure)) return { ...empty, error: `Treemap tiles are parts of a whole, so ${measure.label ?? measure.alias} must add up: use a count, a total (sum) or a percentage of the total.` };
    const values = result.rows.map(row => numberOf(row[measure.alias]));
    if (values.some(v => v != null && v < 0)) return { ...empty, error: 'Treemap tiles are sized by value, so negative values cannot be drawn. Use a bar or waterfall chart.' };
    const notes: string[] = [];
    const blank = values.filter(v => v == null || v === 0).length;
    if (blank) notes.push(`${blank.toLocaleString()} ${blank === 1 ? 'group has' : 'groups have'} no value or zero, so ${blank === 1 ? 'it takes' : 'they take'} no area.`);
    const total = values.reduce<number>((sum, v) => sum + (v ?? 0), 0);
    if (total === 0) return { ...empty, notes, error: 'Every group is zero or empty, so there are no tiles to show. Use the data table.' };
    if (result.truncated) notes.push('This result is partial (more groups match), so tiles are shares of the loaded groups only.');
    const indices = values.map((v, i) => v != null && v > 0 ? i : -1).filter(i => i >= 0);
    if (dims.length === 1) {
        const tiles = indices.map(i => ({ x: rowLabel(result, i, dims, display), y: values[i]!, rowIndex: i as number | null }));
        const { kept, combined } = topWithOther(tiles, limits.tiles ?? 40, rest => ({ x: 'Other', y: rest.reduce((s, t) => s + t.y, 0), rowIndex: null }));
        if (combined) notes.push(`The ${combined.toLocaleString()} smallest groups are combined into Other.`);
        return { series: [{ name: measure.label ?? measure.alias, data: kept }], notes, total };
    }
    const [outer, inner] = dims;
    const groups = new Map<string, { name: string; tiles: TreemapTile[]; y: number }>();
    for (const i of indices) {
        const row = result.rows[i];
        const key = JSON.stringify([typeof row[outer.alias], row[outer.alias] ?? null]);
        const group = groups.get(key) ?? { name: displayCell(row[outer.alias], outer, display, { row }), tiles: [], y: 0 };
        group.tiles.push({ x: displayCell(row[inner.alias], inner, display, { row }), y: values[i]!, rowIndex: i });
        group.y += values[i]!; groups.set(key, group);
    }
    const perGroup = limits.perGroup ?? 12;
    let innerCombined = 0;
    const all = [...groups.values()].map(g => {
        const { kept, combined } = topWithOther(g.tiles, perGroup, rest => ({ x: 'Other', y: rest.reduce((s, t) => s + t.y, 0), rowIndex: null }));
        innerCombined += combined;
        return { name: g.name, tiles: kept, y: g.y };
    });
    const { kept, combined } = topWithOther(all, limits.groups ?? 10, rest => ({ name: 'Other', tiles: [{ x: 'Other', y: rest.reduce((s, g) => s + g.y, 0), rowIndex: null }], y: rest.reduce((s, g) => s + g.y, 0) }));
    if (combined) notes.push(`The ${combined.toLocaleString()} smallest ${outer.label ?? outer.alias} groups are combined into Other.`);
    if (innerCombined) notes.push(`Within each group, the smallest ${inner.label ?? inner.alias} values are combined into Other.`);
    return { series: kept.map(g => ({ name: g.name, data: g.tiles })), notes, total };
}

// ─────────────────────────── Scatter ───────────────────────────

export interface ScatterPoint { x: number; y: number; z?: number; label: string; rowIndex: number }
export const SCATTER_POINT_CAP = 2000;

/**
 * One point per group: the first measure across, the second up, an optional third as bubble size. Groups
 * missing a position (or, for bubbles, a non-negative size) are left out and counted.
 */
export function scatterPoints(result: QueryResult, display: QueryDisplay | null = null, cap = SCATTER_POINT_CAP):
    { points: ScatterPoint[]; skipped: number; capped: number; bubble: boolean; measures: QueryResultColumn[] } {
    const dims = result.columns.filter(c => c.kind === 'dimension');
    const measures = result.columns.filter(c => c.kind === 'measure').slice(0, 3);
    const bubble = measures.length === 3;
    const points: ScatterPoint[] = [];
    let skipped = 0;
    result.rows.forEach((row, index) => {
        const x = numberOf(row[measures[0]?.alias]), y = numberOf(row[measures[1]?.alias]);
        const z = bubble ? numberOf(row[measures[2].alias]) : undefined;
        if (x == null || y == null || (bubble && (z == null || z < 0))) { skipped++; return; }
        points.push({ x, y, ...(bubble ? { z: z! } : {}), label: rowLabel(result, index, dims, display), rowIndex: index });
    });
    const capped = Math.max(0, points.length - cap);
    return { points: capped ? points.slice(0, cap) : points, skipped, capped, bubble, measures };
}

// ─────────────────────────── Gauge ───────────────────────────

export interface GaugeModel {
    value: number;
    target: number;
    progress: GoalProgress;
    state: ThresholdState;
    /** 0–100 for the arc. */
    fill: number;
}

/** Progress of one total against its target, with the threshold tone in the measure's good direction. */
export function gaugeModel(value: number | null, target: number | null | undefined, thresholds: Thresholds | null | undefined,
    format: (n: number) => string): GaugeModel | null {
    if (value == null || target == null || !Number.isFinite(target)) return null;
    const progress = computeGoalProgress(value, target, format);
    if (!progress) return null;
    return { value, target, progress, state: thresholdState(value, thresholds), fill: progress.fillPct };
}
